/**
 * School Levels API Contract (plan 2026-08-20-2253, PR 2).
 *
 * `GET /v1/schools/:school_public_id/school-levels`
 *
 * A school's Blackbaud school levels, each carrying `studentCount` — how many
 * students are actually SEATED at that level right now.
 *
 * The count is the whole reason this endpoint exists. Level-scoped configuration
 * (`attendance.parentAbsence.enabledLevels` / `reasonsByLevel`) must offer the
 * levels a school really uses, and the ONLY safe test for that is seating:
 *
 *  - A NAME-based rule is an outage. Montgomery Bell Academy (school 88) has
 *    exactly ONE level — named "Entire School" — with 1,038 students seated in
 *    it. Excluding that name would leave MBA with zero configurable levels and
 *    kill parent-reported absences for the entire school.
 *  - Seating also correctly drops levels that exist only as history: Riverstone's
 *    0-student "Entire School" and Virginia Academy's retired "Former Middle
 *    School", both of which a name rule would have happily offered.
 *
 * Note that "Entire School" is NOT a Blackbaud wildcard — a type scoped to it is
 * still refused for a student at a different level — so it is offered on exactly
 * the same terms as any other level: does it have students in it.
 *
 * A student is a `SchoolUserProfile` row at this school whose linked
 * `UsersOnSchools` carries `isStudent`, counted by `currentSchoolLevelId`.
 * Students with no profile row or a null level contribute to no level's count,
 * which is correct: they are exactly the population the per-level policy must
 * fail OPEN for rather than configure.
 */

import { z } from 'zod'

/** Path params for the list endpoint. */
export const ListSchoolLevelsRequestSchema = z.object({
  school_public_id: z.string().describe('School unique identifier')
})

export type ListSchoolLevelsRequest = z.infer<typeof ListSchoolLevelsRequestSchema>

/** One school level plus its live student seating. */
export const SchoolLevelSummarySchema = z.object({
  id: z.string().describe('School-level publicId — the key every level-scoped config is keyed by'),
  platformId: z.string().describe('Blackbaud level id'),
  name: z.string().nullable().describe('Level name as Blackbaud reports it, e.g. "Elementary" or "Entire School"'),
  abbreviation: z.string().nullable().describe('Short label, when the platform supplies one'),
  sortOrder: z.number().int().describe('Display order the school configured'),
  studentCount: z
    .number()
    .int()
    .min(0)
    .describe('Students currently seated at this level. 0 means the level is configuration that matches nobody.')
})

export type SchoolLevelSummary = z.infer<typeof SchoolLevelSummarySchema>

export const ListSchoolLevelsResponseSchema = z.object({
  message: z.string(),
  data: z.array(SchoolLevelSummarySchema)
})

export type ListSchoolLevelsResponse = z.infer<typeof ListSchoolLevelsResponseSchema>
